/**
 * React Query Key Factories
 *
 * Hierarchical query keys for inventory domains to enable granular cache invalidation
 */

import type { PaginationParams } from '../api/client';

/**
 * Inventory item query keys
 */
export const inventoryItemKeys = {
  all: ['inventory-items'] as const,
  lists: () => [...inventoryItemKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...inventoryItemKeys.lists(), params] as const,
  details: () => [...inventoryItemKeys.all, 'detail'] as const,
  detail: (id: string) => [...inventoryItemKeys.details(), id] as const,
  byProduct: (productId: string) => [...inventoryItemKeys.all, 'product', productId] as const,
  kpis: () => [...inventoryItemKeys.all, 'kpis'] as const,
  availability: (id: string, quantity: number) =>
    [...inventoryItemKeys.all, id, 'availability', quantity] as const,
};

/**
 * Lot query keys
 */
export const lotKeys = {
  all: ['lots'] as const,
  lists: () => [...lotKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...lotKeys.lists(), params] as const,
  details: () => [...lotKeys.all, 'detail'] as const,
  detail: (id: string) => [...lotKeys.details(), id] as const,
  byInventoryItem: (inventoryItemId: string, params?: PaginationParams) =>
    [...lotKeys.all, 'inventoryItem', inventoryItemId, params] as const,
  expiring: (days: number) => [...lotKeys.all, 'expiring', days] as const,
};

/**
 * Serial query keys
 */
export const serialKeys = {
  all: ['serials'] as const,
  lists: () => [...serialKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...serialKeys.lists(), params] as const,
  details: () => [...serialKeys.all, 'detail'] as const,
  detail: (id: string) => [...serialKeys.details(), id] as const,
  byNumber: (serialNumber: string) => [...serialKeys.all, 'number', serialNumber] as const,
  byInventoryItem: (inventoryItemId: string, params?: PaginationParams) =>
    [...serialKeys.all, 'inventoryItem', inventoryItemId, params] as const,
  byLot: (lotId: string, params?: PaginationParams) =>
    [...serialKeys.all, 'lot', lotId, params] as const,
};

/**
 * Alert query keys
 */
export const alertKeys = {
  all: ['alerts'] as const,
  lists: () => [...alertKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...alertKeys.lists(), params] as const,
  active: () => [...alertKeys.all, 'active'] as const,
};
